import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import NotificationsBell from '../notifications/NotificationsBell';
import DesktopOnlyNotice from './DesktopOnlyNotice';
import MobileSidebar from './MobileSidebar';
import Sidebar from './Sidebar';

const SIDEBAR_COLLAPSED_KEY = 'layout:sidebar-collapsed';

// Páginas que ocupam toda a largura útil (sem max-w + sem padding vertical).
const FULLBLEED_PATHS = ['/operacoes'];

// Telas densas (workbench multi-painel de desktop): no mobile mostram o aviso
// dispensável "otimizada para desktop" — sem bloquear a navegação.
const DESKTOP_OPTIMIZED_PATHS = ['/operacoes', '/sellout'];

const Layout = () => {
    const location = useLocation();
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
        } catch {
            return false;
        }
    });
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, isSidebarCollapsed ? '1' : '0');
        } catch {
            // Ignore localStorage errors in restricted contexts.
        }
    }, [isSidebarCollapsed]);

    const toggleSidebar = () => {
        setSidebarCollapsed(!isSidebarCollapsed);
    };

    // Quando colapsado, hover expande visualmente em overlay (sem deslocar o conteúdo).
    // Quando expandido via toggle, fica fixo expandido.
    const expandedVisually = !isSidebarCollapsed || isHovering;
    const isFullBleed = FULLBLEED_PATHS.some((p) => location.pathname.startsWith(p));
    // Raiz densa da rota atual (ou undefined); `key` remonta o aviso ao trocar de rota.
    const desktopOptimizedRoot = DESKTOP_OPTIMIZED_PATHS.find((p) => location.pathname.startsWith(p));

    return (
        <div className="min-h-dvh bg-mono-50 dark:bg-mono-950">
            <Sidebar
                isCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
                expandedVisually={expandedVisually}
                onHoverChange={setIsHovering}
            />

            <MobileSidebar />

            {desktopOptimizedRoot && <DesktopOnlyNotice key={desktopOptimizedRoot} routeKey={desktopOptimizedRoot} />}

            <div
                className={`flex flex-col flex-1 lg:transition-all lg:duration-300 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}
            >
                {isFullBleed ? (
                    /* Sem sino nas fullbleed: são workbenches 100vh com toolbar
                       própria até a borda direita — qualquer overlay fixo ali
                       intercepta cliques da toolbar, e reservar uma faixa
                       cortaria a altura útil da tela. */
                    <main className="flex-1 pb-20 lg:pb-0 overflow-hidden">
                        <Outlet />
                    </main>
                ) : (
                    <main className="flex-1 pb-20 lg:pb-0">
                        <div className="py-6 md:py-8">
                            {/* O teto era `max-w-7xl` (1280px): descontando a
                                sidebar de 256px e o padding, sobravam ~960px
                                úteis num monitor de 1920 — as tabelas densas
                                rolavam na horizontal enquanto ~600px de tela
                                ficavam vazios nas laterais. 1800px consome a
                                largura inteira em 1920 e ainda segura a linha
                                de texto em telas maiores (2560+), onde correr
                                de ponta a ponta prejudicaria a leitura. */}
                            <div className="max-w-[1800px] mx-auto px-4 md:px-6 lg:px-8">
                                {/* O sino vive NO FLUXO, numa faixa reservada acima
                                    do conteúdo: a versão anterior flutuava `fixed`
                                    no canto superior direito e o botão de 40px
                                    interceptava os cliques das ações do PageHeader
                                    (com max-w-7xl + sidebar, a borda direita do
                                    conteúdo encosta na janela abaixo de ~1600px).
                                    `empty:hidden` some com a faixa quando o Bell
                                    não renderiza (usuário sem a permissão do feed). */}
                                <div className="mb-3 flex justify-end empty:hidden">
                                    <NotificationsBell />
                                </div>
                                <Outlet />
                            </div>
                        </div>
                    </main>
                )}
            </div>
        </div>
    );
};

export default Layout;
